import React, { useEffect, useState, useContext, useRef } from 'react'
import { QuestionnaireCodeContext } from '../Code/QuestionnaireCodeContext';
import { AsyncTypeahead, Token } from 'react-bootstrap-typeahead';
import { Modal, Button } from 'react-bootstrap';
import { getUnidentifiedUser, getQuestionnaireInfo, getQQuestionColumnsInfo, GetAnonymousFormStatus } from '../../services/dataService';
import '../../bootoast/bootoast.css';
import bootoast from '../../bootoast/bootoast';
import * as Constants from '../Common/CommonConstants';

const RecipientSelection = ({ handleUserSelected, ...props }) => {
    const { formState, setFormState, formName, setFormName, stateCode, setStateCode, key1, setKey1, key2, setKey2, error, setUnidentifySub, activeNAICMember, setActiveNAICMember, instanceState, setInstanceState, searchResult, setSearchResult, checkNAICMemberStatus } = useContext(QuestionnaireCodeContext);

    const formId = props.id;
    //console.log("id in RecipientSelection:" + formId);
    const [isLoading, setIsLoading] = useState(false);
    const [options, setOptions] = useState([]);
    const [lookupType, setLookupType] = useState("");
    const [recipientTargetTypeId, setRecipientTargetTypeId] = useState("");
    // LW260805 Co#5394 Key fields modal after policy/file selection (only for a new submission)
    const [keyFieldColumns, setKeyFieldColumns] = useState([]);
    const [keyFieldValues, setKeyFieldValues] = useState({});
    const [showKeyFieldModal, setShowKeyFieldModal] = useState(false);
    const [pendingSelection, setPendingSelection] = useState(null); // { value, formExpired }
    const [headerText, setHeaderText] = useState("");
    // LW260810 Co#5394: show confirmed answers and collapse typeahead after Continue
    const [keyFieldsConfirmed, setKeyFieldsConfirmed] = useState(false);
    const [selectedOption, setSelectedOption] = useState([]);
    const [keyFieldValuesBeforeEdit, setKeyFieldValuesBeforeEdit] = useState(null);
    const typeaheadRef = useRef(null);

    useEffect( () => {
        async function fetchQuestionnaireInfo() {
            if (formId > 0) { 
                const QuestionnaireInfo = await getQuestionnaireInfo(formId);
                const columns = await getQQuestionColumnsInfo(formId);
                //console.log("columns=" + columns);
                if (columns && columns.length > 0) {
                    setKeyFieldColumns(columns);
                    // Pre-select default radio choices when provided by QChoices
                    const defaults = {};
                    columns.forEach(col => {
                        const defaultChoice = (col.qChoices || []).find(c => c.defaultFlag);
                        if (defaultChoice) {
                            defaults[col.tagName] = defaultChoice.choiceCode || defaultChoice.choiceText;
                        }
                    });
                    setKeyFieldValues(defaults);
                } else {
                    setKeyFieldColumns([]);
                    setKeyFieldValues({});
                }
                setShowKeyFieldModal(false);
                setPendingSelection(null);
                setKeyFieldsConfirmed(false);
                setSelectedOption([]);
                setOptions([]);
                const targetRecipientTypeId = QuestionnaireInfo.targetRecipientTypeId;
                setRecipientTargetTypeId(targetRecipientTypeId);
                // LW250701 CO#3613: FEIN, Policy Number, and Bureau File Number, coded for PA only for VFEV Form
                const WCIOStateCode = (QuestionnaireInfo.formWCIOStateCode !== undefined) ? QuestionnaireInfo.formWCIOStateCode : "";
                setStateCode(WCIOStateCode);
                const CanCorrespond = QuestionnaireInfo.isCodeEntryAllowed;
                //console.log("targetRecipientTypeId=" + targetRecipientTypeId);
                setFormName(QuestionnaireInfo.formName);
                setHeaderText(QuestionnaireInfo.description);
                //console.log("formName=" + QuestionnaireInfo.formName);

                if (CanCorrespond) {
                    if (targetRecipientTypeId === Constants.OptiRiskFile)
                        setLookupType("PolicyNumber");
                    else if (targetRecipientTypeId === Constants.Carrier)
                        setLookupType("Carrier");
                    else if (targetRecipientTypeId === Constants.NAICCarrier)
                        setLookupType("NAICCarrier");
                }
                else
                        setLookupType("");
            }
        }
        fetchQuestionnaireInfo();
    }, [formId])

    //LW260304 - CO#4170
    // Track the most recent query to avoid stale updates
    const lastQueryRef = useRef("");

    const setKeyFieldValue = (tagName, value) => {
        setKeyFieldValues(prev => ({ ...prev, [tagName]: value }));
    };

    const allKeyFieldsAnswered = () => {
        if (!keyFieldColumns.length) return true;
        return keyFieldColumns.every(col => {
            const value = keyFieldValues[col.tagName];
            return value !== undefined && value !== null && String(value).trim() !== "";
        });
    };

    // Existing dropdown rows include qInstanceID; "Create New Form" does not
    const isExistingInstance = (value) => {
        return !!(value && (value.qInstanceID || value.qInstanceId || value.QInstanceID));
    };

    const proceedWithSelection = async (value, formExpired, answers) => {
        // Only when creating a NEW submission, block duplicate completed key-field combos
        if (!isExistingInstance(value) && answers && Object.keys(answers).length > 0) {
            try {
                const result = await GetAnonymousFormStatus(formId, value.recipientID, answers);
                if (result && result.data && result.data.status === "COMPLETE") {
                    setInstanceState(Constants.READ_ONLY_MODE);
                    bootoast.toast({
                        position: 'top',
                        message: "Form submission was completed, no further action needed. You may change your answer to submit a new form.",
                        type: 'info',
                        timeout: 10
                    });
                    return;
                }
                else {
                    setInstanceState("");
                }
            } catch (e) {
                // status check is best effort, still let the user open the form
            }
        }
        handleUserSelected(value, formId, formExpired, answers || {});
    };

    const formatKeyFieldDisplay = (col, value) => {
        if (value === undefined || value === null || value === "") return "";
        const choices = col.qChoices || [];
        const match = choices.find(c => (c.choiceCode || c.choiceText) === value);
        return match ? match.choiceText : value;
    };

    const collapseTypeahead = () => {
        setOptions([]);
        if (typeaheadRef.current) {
            // react-bootstrap-typeahead: blur closes the open menu
            if (typeof typeaheadRef.current.blur === "function") {
                typeaheadRef.current.blur();
            } else if (typeaheadRef.current.getInstance && typeof typeaheadRef.current.getInstance().blur === "function") {
                typeaheadRef.current.getInstance().blur();
            }
        }
    };

    const handleKeyFieldContinue = async () => {
        if (!allKeyFieldsAnswered()) {
            bootoast.toast({
                position: 'top',
                message: 'Please answer all questions before continuing.',
                type: 'warning',
                timeout: 5
            });
            return;
        }
        if (!pendingSelection) {
            setShowKeyFieldModal(false);
            return;
        }
        const selection = pendingSelection;
        setShowKeyFieldModal(false);
        setPendingSelection(null);
        setSelectedOption(selection.value ? [selection.value] : []);
        setKeyFieldsConfirmed(true);
        setKeyFieldValuesBeforeEdit(null);
        collapseTypeahead();
        // First Continue and Edit→Continue both apply answers (creates/recreates instance with current key fields)
        await proceedWithSelection(selection.value, selection.formExpired, keyFieldValues);
    };

    const openKeyFieldModalForEdit = () => {
        if (pendingSelection) {
            if (keyFieldsConfirmed) {
                setKeyFieldValuesBeforeEdit({ ...keyFieldValues });
            }
            setShowKeyFieldModal(true);
            return;
        }
        if (selectedOption.length > 0) {
            setPendingSelection({ value: selectedOption[0], formExpired: false });
            if (keyFieldsConfirmed) {
                setKeyFieldValuesBeforeEdit({ ...keyFieldValues });
            }
            setShowKeyFieldModal(true);
        }
    };

    // Show Edit after Continue, or after Back from Create New (answers still required)
    const showKeyFieldSummaryOrEdit =
        keyFieldColumns.length > 0 &&
        !showKeyFieldModal &&
        (keyFieldsConfirmed || !!pendingSelection);

    const renderKeyFieldInput = (col) => {
        const tag = col.tagName;
        const label = col.headerText || tag;
        const choices = col.qChoices || [];
        const isDate = (col.qDataTypeCode || "").toUpperCase() === "DATE" || tag === "ChangeEffectiveDate";
        const isRadio = (col.qInputTypeCode || "").toLowerCase().indexOf("radio") > -1 || choices.length > 0;

        if (isDate) {
            return (
                <div key={tag} style={{ marginBottom: "16px", textAlign: "left" }}>
                    <label className="login-dev-label" style={{ display: "block", marginLeft: 0 }}>
                        <b>{label}{" "}</b>
                        <input
                            type="date"
                            value={keyFieldValues[tag] || ""}
                            onChange={(e) => setKeyFieldValue(tag, e.target.value)}
                        />
                    </label>
                </div>
            );
        }

        if (isRadio) {
            return (
                <div key={tag} style={{ marginBottom: "16px", textAlign: "left" }}>
                    <div className="login-dev-label" style={{ display: "block", marginBottom: "8px", marginLeft: 0 }}>
                        <b>{label}</b>
                    </div>
                    {choices.map((choice) => {
                        //const choiceValue = choice.choiceText || choice.choiceCode;
                        const choiceValue = choice.choiceCode || choice.choiceText;
                        return (
                            <label
                                key={choiceValue}
                                className="login-dev-label"
                                style={{ display: "block", marginLeft: 0, marginBottom: "6px" }}
                            >
                                <input
                                    type="radio"
                                    name={tag}
                                    value={choiceValue}
                                    checked={keyFieldValues[tag] === choiceValue}
                                    onChange={(e) => setKeyFieldValue(tag, e.target.value)}
                                />
                                {choice.choiceText}
                            </label>
                        );
                    })}
                </div>
            );
        }

        return (
            <div key={tag} style={{ marginBottom: "16px", textAlign: "left" }}>
                <label className="login-dev-label" style={{ display: "block", marginLeft: 0 }}>
                    <b>{label}{" "}</b>
                    <input
                        type="text"
                        value={keyFieldValues[tag] || ""}
                        onChange={(e) => setKeyFieldValue(tag, e.target.value)}
                    />
                </label>
            </div>
        );
    };

    const handleSearch = async (query) => {
        lastQueryRef.current = query;       // remember which query this run is for
           setIsLoading(true);
        //console.log("lookupType in handleSearch:" + lookupType + ",stateCode=" + stateCode);
        try {
          // LW251124 - CO#4001 Form prepopulated data does not match the selected policy on the intermediary page
        const items = await getUnidentifiedUser(query, lookupType, formId, stateCode);

        // If the user typed something else while we waited, 
        // the query variable won't match lastQueryRef.current.
        if (query !== lastQueryRef.current) {
            return; // Exit early; this is a stale response
        }
          const options = items.map((i) => ({
                  label: i.label || i.Label,
                  targetID: i.targetID || i.TargetID,
                  recipientID: i.recipientID || i.RecipientID,
                  wCIOStateCode: i.wCIOStateCode || i.WCIOStateCode,
                  formStatus: i.formStatus || i.FormStatus || "",
                  lastUpdated: i.lastUpdated || i.LastUpdated,
                  // LW260806 Co#5394: needed to reopen a specific existing submission
                  qInstanceID: i.qInstanceID || i.qInstanceId || i.QInstanceID || null
          }));

          setOptions(options);
          //setIsLoading(false);

          if (options.length === 0) //no match found
          {
              setSearchResult("nomatch");
              //console.log("nomatch");
          }
          else {

              setSearchResult("");

              // LW260716 Co#5394 Enable Single Submission Feature - Intermediary Page- New Member Application PA& DE 
              //previously if form is complete, popup message 
              //console.log("label, recipientID, targetId,formId, query =" + options[0].label + "," + options[0].recipientID + "," + options[0].targetID + "," + formId + "," + query);
              // ONLY for new membership form
              if (options.length !== 0 && formName.toLowerCase().indexOf(Constants.FormName_MemberShip) > -1) {
                  // check if NAIC code is active member or not

                  var result = await checkNAICMemberStatus(query, stateCode);
                  setActiveNAICMember(result);
                  //console.log("result xxx=[" + result +"]");
                  var message = (stateCode == "07") ? `The code entered is registered to an active member. For assistance, contact membership@dcrb.com.`
                      : `The code entered is registered to an active member. For assistance, contact membership@pcrb.com.`;
                  if (result == "active") {
                      bootoast.toast({
                          position: 'top',
                          message: message,
                          type: 'danger',
                          timeout: 5
                      });
                  }
              }
              // Check form Complete status for one record, like NAIC for new membership
              // Key-field forms are checked after their answers are collected, since the
              // answers are what identify one submission from another
              if (options.length == 1 && keyFieldColumns.length === 0) {
                  // find QInstance and get form status and last updated time
                  //console.log("formId in RecipientSelection:" + formId + ", recipientID=" + options[0].recipientID);
                  var result = await GetAnonymousFormStatus(formId, options[0].recipientID, {});
                  //console.log("options.length=" + options.length);
                  //console.log("result.data.Status=" + result.data.status);
                  if (result.data.status === "COMPLETE") {
                      setInstanceState(Constants.READ_ONLY_MODE);
                      //console.log("formState in RecipientSelection=" + formState);
                      var message = "Form submission was completed, no further action needed.";
                      bootoast.toast({
                          position: 'top',
                          message: message,
                          type: 'info',
                          timeout: 10
                      });
                  }
                  else {
                      setInstanceState("");
                  }
              } 
          }
        } finally {
            // Only stop loading if this was the latest request
            if (query === lastQueryRef.current) {
                setIsLoading(false);
            }
        }
      };
  
    const handleChange = (value) => {
        setIsLoading(false);
        //console.log("handleChange label, recipientID, targetId,formId, query, qInstanceID =" + value.label + "," + value.recipientID + "," + value.targetID + "," + formId + "," + value.qInstanceID);
        if (!value) {
            setSelectedOption([]);
            setKeyFieldsConfirmed(false);
            setPendingSelection(null);
            return;
        }
        //console.log("user selection in handleChange:" + value.label + "," + value.targetID + "," + value.recipientID + "," + value.wCIOStateCode+ "," + value.formStatus + "," + value.lastUpdated  + ", formId/stateCode=" + formId + "/" + stateCode);
        //console.log("id in RecipientSelection:" + formId);
        let formExpired = false;
        setKeyFieldsConfirmed(false);
        setPendingSelection(null);
        setSelectedOption([value]);
        
        if (value.formStatus === "COMPLETE") {
            setInstanceState(Constants.READ_ONLY_MODE);
            var message = "Form Submission was completed, no further action required or needed.";
            bootoast.toast({
                position: 'top',
                message: message,
                type: 'info',
                timeout: 20
            });
        }
        /*
            if (value.formStatus === "INPROG") {
            // LW251218 CO#4080 Intermediary Page and Security Check Points 
            setInstanceState("");
            var lastUpdatedDate = new Date(value.lastUpdated);
            lastUpdatedDate.setHours(0, 0, 0, 0);
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const differenceInMs = today.getTime() - lastUpdatedDate.getTime();
            const oneDayInMs = 1000 * 60 * 60 * 24;
            //console.log("differenceInDays=" + lastUpdatedDate + ", " + today + ", " + differenceInDays + ", REACT_APP_SAVEFORM_RETENTION_DAYS=" + process.env.REACT_APP_SAVEFORM_RETENTION_DAYS);
            // LW251202 CO#4000 Save Feature,  add time limit verbiage  - Make save form retention days configurable
            if (differenceInDays > process.env.REACT_APP_SAVEFORM_RETENTION_DAYS) {
                formExpired = true;
            }
        }
        */
        // LW260806 Co#5394:
        // - Existing row (has qInstanceID): reopen that submission (complete or incomplete)
        // - Create New Form (no qInstanceID): collect key fields, then create
        const existingId = value.qInstanceID || value.qInstanceId || value.QInstanceID || null;
        if (existingId) {
            value.qInstanceID = existingId;
            if (value.formStatus === "COMPLETE") {
                // set state to READ_ONLY_MODE — that disables the Open Form button.
                setInstanceState(Constants.READ_ONLY_MODE);
            }
            else {
                // set state to ""  — that enables the Open Form button.
                setInstanceState("");
            }
            collapseTypeahead();
            proceedWithSelection(value, formExpired, {});
            return;
        }

        if (keyFieldColumns.length > 0) {
            setPendingSelection({ value, formExpired });
            collapseTypeahead();
            setShowKeyFieldModal(true);
            return;
        }

        collapseTypeahead();
        proceedWithSelection(value, formExpired, {});
      };

      const buildPlaceholderText = () => {
        switch (lookupType) {
            case "Carrier":
                return "Enter 5-digit NCCI Carrier Code"
            case "NAICCarrier":
                return "Enter the company's 5-digit NAIC Code"
            case "FEIN":
                return "Enter 9-digit FEIN"
            case "PolicyNumber":
                return "Enter Policy Number without any dashes or spaces"
            case "FileNumber":
                return "Enter Bureau File Number without any dashes or spaces"
            default:
                return "";
        }
      }

      return (
          <form size="lg">
              <div>
                  {stateCode == "37" && <div className="pcrb-logo"></div>}
                  {stateCode == "07" && <div className="dcrb-logo"></div>}
                  {stateCode == "" && <div className="dual-logo"></div>}
                  {/*<h4 className="code-login-welcome">*/}
                  {/*    FormId: {props.id}*/}
                  {/*</h4>*/}
                  {/*<h6 className="code-unidentifylogin-text">Enter code below to access*/}
                  {/*</h6>*/}
                  {formName != "" && 
                  <h6 className="code-login-welcome">
                          <span><b>{formName}</b>&nbsp;&nbsp;</span><span dangerouslySetInnerHTML={{ __html: headerText }} />
                  </h6>}

                  {/* LW260805 Co#5394: answer key fields after policy/file selection, for a new submission only */}
                  <Modal
                      show={showKeyFieldModal}
                      size="lg"
                      backdrop="static"
                      keyboard={false}
                      centered
                      onHide={() => { /* require answers or Back */ }}
                  >
                      <Modal.Header>
                          <Modal.Title>Additional Information Required</Modal.Title>
                      </Modal.Header>
                      <Modal.Body>
                          <p>Please answer the following to continue.</p>
                          {pendingSelection && pendingSelection.value && pendingSelection.value.label && (
                              <p className="login-dev-label" style={{ marginLeft: 0, marginBottom: "12px" }}>
                                  <b>Selected:</b> {pendingSelection.value.label}
                              </p>
                          )}
                          {keyFieldColumns.map(col => renderKeyFieldInput(col))}
                      </Modal.Body>
                      <Modal.Footer>
                          <Button
                              variant="secondary"
                              onClick={() => {
                                  setShowKeyFieldModal(false);
                                  // Keep Create New selection so user can reopen via Edit
                                  if (keyFieldsConfirmed) {
                                      if (keyFieldValuesBeforeEdit) {
                                          setKeyFieldValues(keyFieldValuesBeforeEdit);
                                          setKeyFieldValuesBeforeEdit(null);
                                      }
                                  }
                              }}
                          >
                              Back
                          </Button>
                          <Button
                              variant="primary"
                              disabled={!allKeyFieldsAnswered()}
                              onClick={handleKeyFieldContinue}
                          >
                              Continue
                          </Button>
                      </Modal.Footer>
                  </Modal>

                  {showKeyFieldSummaryOrEdit && (
                      <div style={{ marginBottom: "12px", textAlign: "left" }}>
                          {keyFieldsConfirmed ? (
                              keyFieldColumns.map(col => (
                                  <div key={col.tagName} className="login-dev-label" style={{ marginLeft: 0, marginBottom: "4px" }}>
                                      <b>{col.headerText || col.tagName}:</b>{" "}
                                      {formatKeyFieldDisplay(col, keyFieldValues[col.tagName])}
                                  </div>
                              ))
                          ) : (
                              <div className="login-dev-label" style={{ marginLeft: 0, marginBottom: "4px" }}>
                                  Additional information is required. Click Edit to continue.
                                  {pendingSelection && pendingSelection.value && pendingSelection.value.label && (
                                      <div style={{ marginTop: "4px" }}>
                                          <b>Selected:</b> {pendingSelection.value.label}
                                      </div>
                                  )}
                              </div>
                          )}
                          <button
                              type="button"
                              className="btn btn-link btn-sm"
                              style={{ paddingLeft: 0 }}
                              onClick={openKeyFieldModalForEdit}
                          >
                              Edit
                          </button>
                      </div>
                  )}

                  {
                          (recipientTargetTypeId === Constants.OptiRiskFile || lookupType == "PolicyNumber" || lookupType == "FileNumber") ?
                          (
                          <div>
                          {/*<label className="login-dev-label">*/}
                          {/*<input type="radio" name="userType" value="FEIN" onChange={() => setLookupType("FEIN")} checked={lookupType === "FEIN"} />*/}
                          {/*FEIN*/}
                          {/*</label>*/}
                          <label className="login-dev-label">
                              <input type="radio" name="userType" value="PolicyNumber" onChange={() => setLookupType("PolicyNumber")} checked={lookupType === "PolicyNumber"} />
                              Policy Number
                          </label>
                          <label className="login-dev-label">
                              <input type="radio" name="userType" value="FileNumber" onChange={() => setLookupType("FileNumber")} checked={lookupType === "FileNumber"} />
                              Bureau File Number
                          </label>
                          </div>
                      ) : ""
                  }
            <AsyncTypeahead
              className= "fadeIn first" 
              id="search"
              ref={typeaheadRef}
              filterBy={() => true}   //LW260225 - CO#4170 
              delay={800}             //LW260304 - CO#4170, 4533
              emptyLabel={"No match found."}
              isLoading={isLoading}
              labelKey={(option) => `${option.label}`}
              minLength={lookupType === "NAICCarrier" ? 5 : lookupType === "FEIN" ? 5 : 1}
              onSearch={handleSearch}
              onChange={(value)=>handleChange(value[0])}
              selected={selectedOption}
              useCache={false}
              options={options}
              placeholder = {buildPlaceholderText()}
              renderToken ={ (option,  index) => (
                <Token
                    key={index}
                    option={option}
                    onRemove={() => {
                        setSelectedOption([]);
                        setKeyFieldsConfirmed(false);
                        setPendingSelection(null);
                    }}
                >
                    {option.label}
                </Token>
              )}

                  />
                  <br /><br/>
                  {(formName.toLowerCase().indexOf(Constants.FormName_MemberShip) > -1 && formName.toUpperCase().indexOf("PCRB") > -1) && <div> For assistance, contact Membership Services at <a href="mailto:membership@pcrb.com?subject=PA New Member Application Form Inquiry">membership@pcrb.com.</a></div>}
                  {(formName.toLowerCase().indexOf(Constants.FormName_MemberShip) > -1 && formName.toUpperCase().indexOf("DCRB") > -1) && <div> For assistance, contact Membership Services at <a href="mailto:membership@dcrb.com?subject=DE New Member Application Form Inquiry">membership@dcrb.com.</a></div>}
                  {formName.toLowerCase().indexOf(Constants.FormName_Ownership) > -1 && <div>For assistance, contact the Underwriting team at <br /><a href="mailto:underwritinginquiries@pcrb.com?subject=Ownership Form Inquiry">underwritinginquiries@pcrb.com</a> or 215-320-4422.</div>}
                  {formName.toLowerCase().indexOf(Constants.FormName_VFEF) > -1 && <div>For assistance, contact the Classifications team at <br /><a href="mailto:classinquiries@pcrb.com?subject=Volunteer Firemen Exposure Form Inquiry">classinquiries@pcrb.com</a> or 215-320-4488.</div>}
                  &nbsp;&nbsp;
                  <br/>
                  {/*<a target="_blank" rel="noopener noreferrer" href="https://www.pcrb.com/about-us/contact-us#heading_1">Need more Help?</a>*/}
              </div>
      </form>
  );
};

export default RecipientSelection;